import { Box, Text } from '@factorialco/deprecated-design-system'

import i18n from 'modules/core/lib/i18n'

import { CollapsableIconCard, LeftIconProps } from 'modules/processes/components/CollpsableIconCard'

import { TaskDetail } from '../../TaskElement/useTaskDetail'
import { UploadDocumentsComponent } from '../UploadDocuments/UploadDocumentsComponent'
import { useUploadDocumentFiles } from '../UploadDocuments/useUploadDocumentFiles'

export const IDAndSocialSecurityUploads = ({
  task,
  employeeId,
  folderConfiguration,
  canEmployeeManageAnyEmployeeFolder,
}: {
  task: TaskDetail
  employeeId: number
  folderConfiguration?: {
    folder_id?: string
    onboarding_folder_id?: number
    field_ids?: number[]
  }
  canEmployeeManageAnyEmployeeFolder: boolean
}) => {
  const folderId = Number(folderConfiguration?.folder_id)
  const onboardingFolderId = folderConfiguration?.onboarding_folder_id

  return (
    <>
      {Boolean(folderId) && (
        <Box gap="s12" paddingY="s8">
          <Box>
            <Text.Normal weight="semibold" color="grey1000">
              {i18n.t('processes.process_settings.wizard.tasks.types.request_id.preview.upload_id')}
            </Text.Normal>
            <Box paddingTop="s6" flexDirection="row">
              <Text.Normal>
                {i18n.t(
                  'processes.process_settings.wizard.tasks.types.request_id.preview.id_copy.first'
                )}
              </Text.Normal>

              <Box paddingLeft="s4">
                <Text.Normal weight="semibold">
                  {i18n.t(
                    'processes.process_settings.wizard.tasks.types.request_id.preview.id_copy.second'
                  )}
                </Text.Normal>
              </Box>
            </Box>
            <Box padding="s0" flexDirection="row">
              <Text.Normal>
                {i18n.t(
                  'processes.process_settings.wizard.tasks.types.request_id.preview.store_copy.first'
                )}
              </Text.Normal>

              <Box paddingLeft="s4">
                <Text.Normal weight="semibold">
                  {i18n.t(
                    'processes.process_settings.wizard.tasks.types.request_id.preview.store_copy.second'
                  )}
                </Text.Normal>
              </Box>
            </Box>
          </Box>
          <FrontIDCard
            task={task}
            employeeId={employeeId}
            folderId={folderId}
            canEmployeeManageAnyEmployeeFolder={canEmployeeManageAnyEmployeeFolder}
          />
          <BackIDCard
            task={task}
            employeeId={employeeId}
            folderId={folderId}
            canEmployeeManageAnyEmployeeFolder={canEmployeeManageAnyEmployeeFolder}
          />
        </Box>
      )}
      {Boolean(onboardingFolderId) && (
        <SocialSecurityCertificate
          task={task}
          employeeId={employeeId}
          folderId={onboardingFolderId}
          canEmployeeManageAnyEmployeeFolder={canEmployeeManageAnyEmployeeFolder}
        />
      )}
    </>
  )
}

interface CertificateProps {
  task: TaskDetail
  employeeId: number
  folderId?: number
  canEmployeeManageAnyEmployeeFolder: boolean
}

const SocialSecurityCertificate = ({
  task,
  employeeId,
  folderId,
  canEmployeeManageAnyEmployeeFolder,
}: CertificateProps) => {
  return (
    <Box gap="s12">
      <Box>
        <Text.Normal weight="semibold" color="grey1000">
          {i18n.t(
            'processes.process_settings.tasks.form.super_tasks_module.data_collection.social_security_certificate.title'
          )}
        </Text.Normal>
        <Box paddingTop="s4" flexDirection="row">
          <Text.Normal>
            {i18n.t(
              'processes.process_settings.tasks.form.super_tasks_module.data_collection.social_security_certificate.copy.first'
            )}
          </Text.Normal>

          <Box paddingLeft="s4">
            <Text.Normal weight="semibold">
              {i18n.t(
                'processes.process_settings.tasks.form.super_tasks_module.data_collection.social_security_certificate.copy.second'
              )}
            </Text.Normal>
          </Box>
        </Box>
        <Box flexDirection="row" gap="s4">
          <Text.Normal>
            {i18n.t(
              'processes.process_settings.tasks.form.super_tasks_module.data_collection.social_security_certificate.folder.first'
            )}
          </Text.Normal>
          <Text.Normal weight="semibold">
            {i18n.t(
              'processes.process_settings.tasks.form.super_tasks_module.data_collection.social_security_certificate.folder.type'
            )}
          </Text.Normal>
          <Text.Normal>
            {i18n.t(
              'processes.process_settings.tasks.form.super_tasks_module.data_collection.social_security_certificate.folder.where'
            )}
          </Text.Normal>
        </Box>
      </Box>
      <CollapsableDragAndDropCard
        task={task}
        employeeId={employeeId}
        documentName="social_security_certificate"
        folderId={folderId}
        title={i18n.t(
          'processes.process_settings.tasks.form.super_tasks_module.data_collection.social_security_certificate.accreditation'
        )}
        cta={i18n.t(
          'processes.process_settings.tasks.form.super_tasks_module.data_collection.social_security_certificate.cta'
        )}
        subtitle={i18n.t(
          'processes.process_settings.tasks.form.super_tasks_module.data_collection.social_security_certificate.subtitle'
        )}
        leftIcon={{
          icon: 'edit-file-filled',
          color: 'purple1000',
          background: 'purple200',
        }}
        canEmployeeManageAnyEmployeeFolder={canEmployeeManageAnyEmployeeFolder}
      />
    </Box>
  )
}

const FrontIDCard = ({
  task,
  employeeId,
  folderId,
  canEmployeeManageAnyEmployeeFolder,
}: CertificateProps) => {
  return (
    <CollapsableDragAndDropCard
      task={task}
      employeeId={employeeId}
      folderId={folderId}
      documentName="id_document_front"
      title={i18n.t(
        'processes.process_settings.tasks.form.super_tasks_module.data_collection.id_certificate.front'
      )}
      cta={i18n.t(
        'processes.process_settings.tasks.form.super_tasks_module.data_collection.id_certificate.cta'
      )}
      subtitle={i18n.t(
        'processes.process_settings.tasks.form.super_tasks_module.data_collection.id_certificate.front_subtitle'
      )}
      leftIcon={{
        icon: 'id',
        color: 'viridian1000',
        background: 'viridian200',
      }}
      canEmployeeManageAnyEmployeeFolder={canEmployeeManageAnyEmployeeFolder}
    />
  )
}

const BackIDCard = ({
  task,
  employeeId,
  folderId,
  canEmployeeManageAnyEmployeeFolder,
}: CertificateProps) => {
  return (
    <CollapsableDragAndDropCard
      task={task}
      employeeId={employeeId}
      folderId={folderId}
      documentName="id_document_back"
      title={i18n.t(
        'processes.process_settings.tasks.form.super_tasks_module.data_collection.id_certificate.back'
      )}
      cta={i18n.t(
        'processes.process_settings.tasks.form.super_tasks_module.data_collection.id_certificate.cta'
      )}
      subtitle={i18n.t(
        'processes.process_settings.tasks.form.super_tasks_module.data_collection.id_certificate.back_subtitle'
      )}
      leftIcon={{
        icon: 'id-back',
        color: 'viridian1000',
        background: 'viridian200',
      }}
      canEmployeeManageAnyEmployeeFolder={canEmployeeManageAnyEmployeeFolder}
    />
  )
}

interface CollapsableDragAndDropCardProps extends CertificateProps {
  title: string
  subtitle: string
  cta: string
  documentName: string
  leftIcon: LeftIconProps
  canEmployeeManageAnyEmployeeFolder: boolean
}

const CollapsableDragAndDropCard = ({
  task,
  employeeId,
  folderId,
  title,
  subtitle,
  documentName,
  cta,
  leftIcon,
  canEmployeeManageAnyEmployeeFolder,
}: CollapsableDragAndDropCardProps) => {
  const { loading, fetchedFiles, employee, canEditEmployeeDocuments, onAddFiles, fileOptions } =
    useUploadDocumentFiles({
      task,
      employeeId,
      documentName,
      folderId,
      canEmployeeManageAnyEmployeeFolder,
    })

  const fileUploaded = fetchedFiles.length === 1 && !loading
  const fileName = fileOptions[0]?.fileName

  return (
    <CollapsableIconCard
      title={title}
      subtitle={fileName}
      cta={cta}
      completed={fileUploaded}
      leftIcon={leftIcon}
    >
      <UploadDocumentsComponent
        employee={employee}
        fileSubtitle={subtitle}
        loading={loading}
        canEditEmployeeDocuments={canEditEmployeeDocuments}
        disabled={fileUploaded}
        onAddFiles={onAddFiles}
        fileOptions={fileOptions}
      />
    </CollapsableIconCard>
  )
}
